import useFeatureFlag from '../../common/use-feature-flag';
import { GROUP_ALIAS_TYPES } from '../content/helpers';
import { Feature } from '../features/features';
import { Comment } from './types';

type CommentAuthorSource = Pick<Comment, 'author' | 'authorAlias'>;

// An aliased comment displays the alias, never the advocate behind it.
export function getCommentDisplayAuthor(
  comment: CommentAuthorSource
): Comment['author'] {
  const alias = comment.authorAlias;
  if (!alias) return comment.author;

  return {
    id: '',
    name: alias.displayName,
    avatar: { url: alias.avatarUrl || '', color: '' },
    privateProfile: false,
  };
}

// Mirrors useContentAuthorLink (models/content/helpers.ts) so a comment author
// and a content author resolve an alias the same way. Returns null for "render
// plain text", which is the pre-ticket behaviour and the safe fallback whenever
// the alias has no resolvable target.
export function useCommentAuthorLink(
  comment: CommentAuthorSource | null | undefined
): string | null {
  const authorAliasNavEnabled = !!useFeatureFlag(Feature.AUTHOR_ALIAS_NAV_FIX);

  if (!comment) return null;

  const alias = comment.authorAlias;

  if (alias) {
    if (!authorAliasNavEnabled) return null;

    if ((GROUP_ALIAS_TYPES as readonly string[]).includes(alias.aliasType)) {
      return alias.id ? `/aliases/${alias.id}` : null;
    }
    // Allowlist, not a fallthrough: an unrecognised or deleted alias type must
    // not resolve to a profile. A user alias links to the person it stands for,
    // never to the advocate who posted under it.
    if (alias.aliasType === 'user') {
      return alias.userId ? `/profiles/${alias.userId}` : null;
    }
    return null;
  }

  return comment.author?.id ? `/profiles/${comment.author.id}` : null;
}
